import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Layer, Position } from '@deck.gl/core';
import { TextLayer } from '@deck.gl/layers';
import {
  DrawPolygonMode,
  DrawCircleFromCenterMode,
  EditableGeoJsonLayer,
  ViewMode,
  ModifyMode,
  CompositeMode,
  TranslateMode,
  type EditAction,
  type GeoJsonEditMode,
} from '@deck.gl-community/editable-layers';
import type { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';
import { v4 as uuidV4 } from 'uuid';
import type { CustomArea } from '../../../types/domain';
import { useCustomAreas } from '../useCustomAreas';
import { useCustomAreaCreation } from '../useCustomAreaCreation';
import { useCustomAreaEdition } from '../useCustomAreaEdition';
import { useMapSelection } from '../useMapSelection';
import { alpha, toRgb } from '../../../utils/color';
import {
  calculateCentroid,
  calculateDistance,
  simplifyPolygon,
  truncatePolygon,
} from '../../../utils/geospatial';
import { mapboxApiClient } from '../../../infrastructure/mapbox/client';
import { isValidMinutes } from '../../../constants/customArea';
import { useDataFilter } from '../useDataFilter';

type CustomFeature = Feature<Polygon> & { key: string; name: string };

type CustomFeatureCollection = FeatureCollection<Geometry> & {
  features: CustomFeature[];
};

type UseCustomAreaLayer = {
  customAreaLayer: Layer;
  customAreaLabelLayer: Layer;
};

const NUMBER_OF_POINTS_FOR_CIRCLE = 16;

const generateNextCustomAreaLabel = (
  customAreas: CustomArea[],
  areaNamePrefix: string
): string => {
  // nosemgrep
  const regex = new RegExp(`^${areaNamePrefix}([1-9][0-9]*)$`);
  const largestNumber = Math.max(
    ...customAreas.map((customArea: CustomArea): number => {
      const matches = regex.exec(customArea.label);
      return matches ? Number(matches[1]) : 0;
    }),
    0
  );
  return `${areaNamePrefix}${largestNumber + 1}`;
};

const useCustomAreaLayer = (): UseCustomAreaLayer => {
  const { filteredCustomAreaKeys } = useDataFilter();
  const { t } = useTranslation();
  const { visibleCustomAreas, addCustomArea, customAreas } = useCustomAreas();
  const { creatingCustomArea, endCustomAreaCreation } = useCustomAreaCreation();
  const { editingCustomArea, editCustomArea } = useCustomAreaEdition();
  const { selectionKind, selectedCustomAreaKeys } = useMapSelection();

  const isMovingPosition = useRef<boolean>(false);
  const isPressingMetaKey = useRef<boolean>(false);

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.metaKey) {
        isPressingMetaKey.current = true;
      }
    };
    const handleKeyUp = (event: KeyboardEvent): void => {
      isPressingMetaKey.current = event.ctrlKey || event.metaKey;
    };
    const handleWindowBlur = (): void => {
      isPressingMetaKey.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const data = useMemo((): CustomFeatureCollection => {
    return {
      type: 'FeatureCollection',
      features: visibleCustomAreas.map(
        (customArea: CustomArea): CustomFeature => {
          return {
            key: customArea.key,
            type: 'Feature',
            name: customArea.label,
            geometry: {
              type: 'Polygon',
              coordinates: [customArea.geometry.coordinates],
            },
            properties: {},
          };
        }
      ),
    };
  }, [visibleCustomAreas]);

  const editMode = useMemo(() => {
    if (creatingCustomArea) {
      if (creatingCustomArea.type === 'circle') {
        return DrawCircleFromCenterMode;
      }
      if (creatingCustomArea.type === 'walking') {
        if (isValidMinutes(creatingCustomArea.minutes)) {
          return DrawPolygonMode;
        }
        return ViewMode;
      }
      return DrawPolygonMode;
    }
    if (editingCustomArea) {
      if (editingCustomArea.type === 'polygon') {
        return new CompositeMode([
          new TranslateMode() as unknown as GeoJsonEditMode,
          new ModifyMode() as unknown as GeoJsonEditMode,
        ]);
      }
      if (editingCustomArea.type === 'circle') {
        return TranslateMode;
      }
      return ViewMode;
    }
    return ViewMode;
  }, [creatingCustomArea, editingCustomArea]);

  const currentlyEditingAreas = useMemo((): number[] => {
    if (editingCustomArea) {
      return [
        data.features.findIndex((feature: CustomFeature): boolean => {
          return feature.key === editingCustomArea.key;
        }),
      ];
    }
    return [];
  }, [data, editingCustomArea]);

  const handleEdit = useCallback(
    async (event: EditAction<FeatureCollection>): Promise<void> => {
      const { editType, editContext, updatedData } = event;
      const geometry =
        updatedData.features[editContext.featureIndexes?.[0] || 0]?.geometry;
      if (geometry && geometry.type !== 'Polygon') {
        return;
      }

      if (creatingCustomArea) {
        // 新規作成の場合
        if (creatingCustomArea.type === 'polygon') {
          if (editType === 'addFeature') {
            addCustomArea({
              key: uuidV4(),
              type: 'polygon',
              center: calculateCentroid(geometry),
              radius: 0,
              minutes: 0,
              label: generateNextCustomAreaLabel(customAreas, t('area')),
              geometry: {
                coordinates: truncatePolygon(geometry).coordinates[0],
              },
            });
            endCustomAreaCreation();
          }
        } else if (creatingCustomArea.type === 'circle') {
          if (editType === 'addFeature') {
            const center = calculateCentroid(geometry);
            const distance = Math.round(
              calculateDistance(center, geometry.coordinates[0][0], 'meters')
            );

            addCustomArea({
              key: uuidV4(),
              type: 'circle',
              center: center,
              radius: distance / 1000,
              label: generateNextCustomAreaLabel(customAreas, t('area')),
              minutes: 0,
              geometry: {
                coordinates: truncatePolygon(geometry).coordinates[0],
              },
            });
            endCustomAreaCreation();
          }
        } else if (creatingCustomArea.type === 'walking') {
          if (editType === 'addTentativePosition') {
            const polygon = await mapboxApiClient.getIsochrone(
              editContext.position,
              creatingCustomArea.minutes,
              'walking'
            );
            if (polygon) {
              addCustomArea({
                key: uuidV4(),
                type: 'walking',
                center: editContext.position,
                radius: 0,
                minutes: creatingCustomArea.minutes,
                label: generateNextCustomAreaLabel(customAreas, t('area')),
                geometry: {
                  coordinates: truncatePolygon(simplifyPolygon(polygon))
                    .coordinates[0],
                },
              });
              endCustomAreaCreation();
            }
          }
        }
      } else if (editingCustomArea) {
        // 変更の場合
        if (editingCustomArea.type === 'polygon') {
          if (editType === 'movePosition' || editType === 'translating') {
            if (editType === 'movePosition') {
              isMovingPosition.current = true;
            }
            if (editType === 'translating' && isMovingPosition.current) {
              // CompositeModeを使用していると、頂点の編集(movePosition)中にも移動(translate)のイベントが発火してしまうため無視する
              return;
            }

            // 編集中・移動中は履歴に追加せずに更新し、完了時に履歴に追加する
            editCustomArea(
              {
                center: calculateCentroid(geometry),
                geometry: {
                  coordinates: truncatePolygon(geometry).coordinates[0],
                },
              },
              false
            );
          } else if (
            editType === 'finishMovePosition' ||
            editType === 'translated'
          ) {
            if (editType === 'finishMovePosition') {
              isMovingPosition.current = false;
            }
            if (editType === 'translated' && isMovingPosition.current) {
              // CompositeModeを使用していると、頂点の編集(movePosition)中にも移動(translate)のイベントが発火してしまうため無視する
              return;
            }

            editCustomArea(
              {
                center: calculateCentroid(geometry),
                geometry: {
                  coordinates: truncatePolygon(geometry).coordinates[0],
                },
              },
              true
            );
          } else if (
            editType === 'addPosition' ||
            editType === 'removePosition'
          ) {
            // 誤操作防止のため、Ctrl/Cmdキーを押していない場合は無視する
            if (!isPressingMetaKey.current) {
              return;
            }
            editCustomArea(
              {
                center: calculateCentroid(geometry),
                geometry: {
                  coordinates: truncatePolygon(geometry).coordinates[0],
                },
              },
              true
            );
          }
        } else if (editingCustomArea.type === 'circle') {
          if (editType === 'translating') {
            // 移動中は履歴に追加せずに更新する
            editCustomArea(
              {
                center: calculateCentroid(geometry),
                geometry: {
                  coordinates: truncatePolygon(geometry).coordinates[0],
                },
              },
              false
            );
          } else if (editType === 'translated') {
            editCustomArea(
              {
                center: calculateCentroid(geometry),
                geometry: {
                  coordinates: truncatePolygon(geometry).coordinates[0],
                },
              },
              true
            );
          }
        } else if (editingCustomArea.type === 'walking') {
          // 徒歩圏のカスタムエリアは図形的な編集ができないため何もしない
        }
      }
    },
    [
      t,
      creatingCustomArea,
      customAreas,
      editingCustomArea,
      isMovingPosition,
      addCustomArea,
      endCustomAreaCreation,
      editCustomArea,
    ]
  );

  // EditableGeoJsonLayerはDeck.glの内部構造上、updateTriggersの参照を安定させないと全アクセサが再評価されるためメモ化する
  const customAreaLayer = useMemo((): Layer => {
    return new EditableGeoJsonLayer({
      id: 'custom-area-layer',
      data: data,
      mode: editMode,
      modeConfig:
        editMode === DrawCircleFromCenterMode
          ? {
              steps: NUMBER_OF_POINTS_FOR_CIRCLE,
              formatTooltip: (): string => '',
            }
          : undefined,
      pickable: selectionKind !== 'link',
      selectedFeatureIndexes: currentlyEditingAreas,
      getEditHandlePointColor: [237, 26, 61, 255], // #ED1A3F
      getEditHandlePointOutlineColor: [255, 255, 255, 255], // #FFFFFF
      getEditHandlePointRadius: 5,
      getTentativeLineColor: [237, 26, 61, 127], // #ED1A3F 50%
      getTentativeFillColor: [237, 26, 61, 51], // #ED1A3F 20%
      _subLayerProps: {
        geojson: {
          getFillColor: (feature: CustomFeature): number[] => {
            if (selectedCustomAreaKeys.includes(feature.key)) {
              // クリックで選択されたカスタムエリアを強調表示する
              return toRgb(alpha('#848C12', 0.6));
            }
            if (filteredCustomAreaKeys.includes(feature.key)) {
              // フィルターの対象になっているカスタムエリアを強調表示する
              return toRgb(alpha('#595959', 0.6));
            }
            if (feature.key === editingCustomArea?.key) {
              // 編集中のカスタムエリアは強調表示する
              return toRgb(alpha('#ED1A3F', 0.2));
            }
            return [91, 133, 232, 51]; // #5B85E8 20%
          },
          getLineColor: (feature: CustomFeature): number[] => {
            if (selectedCustomAreaKeys.includes(feature.key)) {
              // クリックで選択されたカスタムエリアを強調表示する
              return toRgb(alpha('#F2FF21', 0.3));
            }
            if (filteredCustomAreaKeys.includes(feature.key)) {
              // フィルターの対象になっているカスタムエリアを強調表示する
              return toRgb(alpha('#8C8C8C', 0.5));
            }
            if (feature.key === editingCustomArea?.key) {
              return toRgb(alpha('#ED1A3F', 0.5));
            }
            return toRgb(alpha('#5B85E8', 0.5));
          },
          getLineWidth: 2,
          lineWidthUnits: 'pixels',
          updateTriggers: {
            getFillColor: [
              selectedCustomAreaKeys,
              filteredCustomAreaKeys,
              editingCustomArea?.key,
            ],
            getLineColor: [
              selectedCustomAreaKeys,
              filteredCustomAreaKeys,
              editingCustomArea?.key,
            ],
          },
        },
      },
      onEdit: handleEdit,
    });
  }, [
    data,
    editMode,
    currentlyEditingAreas,
    editingCustomArea,
    selectedCustomAreaKeys,
    filteredCustomAreaKeys,
    selectionKind,
    handleEdit,
  ]);

  const customAreaLabelLayer = new TextLayer({
    id: 'polygon-label-layer',
    data: data.features,
    background: true,
    backgroundPadding: [16, 4],
    getPosition: (feature: CustomFeature): Position => {
      if (feature.geometry.type !== 'Polygon') {
        return [Number.NaN, Number.NaN];
      }
      const [longitude, latitude] = calculateCentroid(feature.geometry);
      return [longitude, latitude];
    },
    getText: (feature: CustomFeature): string => {
      return feature.name;
    },
    getSize: 16,
    getColor: [249, 249, 249],
    getBackgroundColor: [150, 150, 150, 130],
    characterSet: 'auto',
  });

  return {
    customAreaLayer,
    customAreaLabelLayer,
  };
};

export { useCustomAreaLayer };
